import {Component, OnInit, signal, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {InfoPageHeaderComponent} from '../../shared/components/info-page-header/info-page-header.component';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {Router, RouterLink} from '@angular/router';
import {HoomvipStorage} from '../../core/hoomvip-storage/hoomvip-storage.service';
import {SignaturePadComponent} from '../../shared/components/signature-pad/signature-pad.component';
import {SignatureService} from '../../core/api/signature.service';
import {ContractTypeService} from '../../core/api/contract-type.service';
import {ContractType} from '../../shared/models/contract-type.model';
import {ModalErrorService} from '../../core/services/modal-error.service';
import {MetricsService} from '../../core/services/metrics.service';
import {CheckInValidators} from '../../core/services/check-in.validators';
import {requiresContract} from '../../shared/models/reservation.model';
import {ReservationService} from '../../core/api/reservation.service';
import {catchError, map} from 'rxjs/operators';
import {Observable, of} from 'rxjs';

@Component({
  selector: 'app-check-in-signature',
  templateUrl: './check-in-signature.component.html',
  styleUrls: ['./check-in-signature.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, InfoPageHeaderComponent, RouterLink, SignaturePadComponent],
})
export class CheckInSignatureComponent implements OnInit {
  contractUrl: string | null = null;
  form: FormGroup;
  contractTypes: ContractType[] = [];
  @ViewChild('signaturePad') signaturePad!: SignaturePadComponent;
  loading = signal(false);
  contractReadRequired = signal(false);

  constructor(
    protected fb: FormBuilder,
    protected router: Router,
    private contractTypeService: ContractTypeService,
    protected hoomvipStorage: HoomvipStorage,
    private signatureService: SignatureService,
    protected modalErrorService: ModalErrorService,
    protected metricsService: MetricsService,
    private reservationService: ReservationService,
  ) {

    this.form = this.fb.group({
      email: ['', [Validators.required, CheckInValidators.strictEmail]],
      aceptaPrivacidad: [false, Validators.requiredTrue],
      aceptaCondiciones: [false, Validators.requiredTrue],
    });
  }

  ngOnInit(): void {
    this.contractUrl = this.hoomvipStorage.getCheckInContractUrl();
    console.log('checkInResponse recibido:', this.contractUrl);

    this.shouldRequireContractRead().subscribe((required) => {
      this.contractReadRequired.set(required);

      if (required) {
        if (!this.form.get('aceptaCondiciones')) {
          this.form.addControl('aceptaCondiciones', this.fb.control(false, Validators.requiredTrue));
        }
      } else {
        this.form.removeControl('aceptaCondiciones');
      }

      this.form.updateValueAndValidity();
    });

    if (this.hoomvipStorage.getCheckInEmail() === null || this.hoomvipStorage.getCheckInEmail() === '') {
      this.enqueueEmailRequiredModal();
    } else {
      this.form.patchValue({email: this.hoomvipStorage.getCheckInEmail()});
    }

    console.log('email recibido:', this.form.get('email')?.value);

    this.contractTypeService.getContractTypes().subscribe({
      next: (types) => this.contractTypes = Array.isArray(types) ? types : [],
      error: (err) => {
        console.error('Error cargando tipos de contrato:', err);
        this.contractTypes = [];
      }
    });
  }

  protected shouldRequireContractRead(): Observable<boolean> {
    return this.reservationService.getReservation().pipe(
      map((reservation) => requiresContract(reservation)),
      catchError((err) => {
        console.error('Error cargando reserva para determinar si se requiere contrato:', err);
        return of(false);
      })
    );
  }

  onContractClick(url: string, event: MouseEvent): void {
    event.preventDefault();
    this.storeEmail();
    this.hoomvipStorage.setContractRead();
    this.router.navigate(['/contract'], {queryParams: {url: url}});

  }

  validateSubmit(): boolean {
    if (!this.signaturePad.hasSignature()) {
      console.log('No hay firma');
      this.enqueueSignatureRequiredModal();
      return false;
    }
    if (this.contractReadRequired() && !this.hoomvipStorage.isContractRead()) {
      this.enqueueReadContractRequiredModal();
      return false;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return false;
    }

    return true;
  }

  onSubmit(): void {
    if(!this.validateSubmit()) {
      return;
    }
    this.loading.set(true);
    const signature = {
      travel_register_id: this.hoomvipStorage.getCheckInTravelRegisterId() ?? '',
      signature: this.signaturePad.toDataUrl(),
      email: this.form.get('email')?.value ?? this.hoomvipStorage.getCheckInEmail() ?? '',
    };

    console.log('Firma a guardar:', signature);

    this.signatureService.setSignature(signature).subscribe({
        next: (signatureResponse: any) => {
          this.metricsService.checkInSigned();
          this.loading.set(false);
          this.checkInEnd(signatureResponse);
        },
        error:
          (error) => {
            this.loading.set(false);
            console.error('Error en una de las operaciones de check-in:', error);
            console.log('status', error.status);
            console.log('backend message', error.error?.message);
            console.log('backend errors', error.error?.errors);
          }
      }
    );
  }

  showEmailField(): boolean {
    const shouldShow = this.hoomvipStorage.getShowCheckInEmailDialog();
    if (shouldShow) {
      return true;
    }
    return this.hoomvipStorage.getCheckInEmail() === null || this.hoomvipStorage.getCheckInEmail() === '';
  }

  isFieldInvalid(fieldname: string): boolean {
    const field = this.form.get(fieldname);
    return !!field && field.invalid && (field.touched || field.dirty);
  }

  private enqueueSignatureRequiredModal() {
    this.modalErrorService.enqueue('Firma requerida', 'Debe firmar el documento para continuar');
  }

  private enqueueReadContractRequiredModal() {
    this.modalErrorService.enqueue('Contrato requerido', [
      'Antes de firmar, debes leer el contrato de estancia.',
      'La firma implica que aceptas todas las condiciones establecidas en el documento.',
      'Por favor, revisa el contrato completo antes de proceder.',
    ]);
  }

  private enqueueEmailRequiredModal() {
    this.modalErrorService.enqueue('Email requerido', [
      'Antes de firmar, debes ingresar tu email.',
    ]);
  }

  protected checkInEnd(signatureResponse: any) {
    console.log('Firma guardada correctamente', signatureResponse);
    this.hoomvipStorage.setSignatureInfo(signatureResponse.contractUrl, signatureResponse.travelRegisterFileURL);
    this.router.navigate(['/check-in-end'], {
      state: {
        contractUrl: signatureResponse.contractUrl,
        travelRegisterFileURL: signatureResponse.travelRegisterFileURL
      }
    });
  }

  protected storeEmail() {
    const email = this.form.get('email')?.value;
    if (email) {
      this.hoomvipStorage.setCheckInEmail(email);
    }
  }
}
